
// src/App.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import AppRoutes from './routes/AppRoutes';
import AppLayout from './layouts/AppLayout';
import PageLayout from './layouts/PageLayout';

import companyOptions from './data/companies.json';
import { getCompaniesForExchange } from './data/exchangeConfig';
import { useUser } from './UserContext';

import { useBackendPing } from './hooks/useBackendPing';
import { useMetricsData } from './hooks/useMetricsData';

function App() {
  const navigate = useNavigate();

  // -----------------------
  // Auth / identity
  // -----------------------
  const { userEmail, setUserEmail } = useUser();
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const handleLogout = () => {
    setIsLoggedIn(false);
    setUserEmail('');
    localStorage.removeItem('userEmail');
  };

  // -----------------------
  // Backend config + ping
  // -----------------------
  const backendOrigin =
    process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000';

  const { pingBackend } = useBackendPing(backendOrigin);

  // Ping backend on every route change (wakes sleeping backend)
  const location = useLocation();
  useEffect(() => {
    pingBackend();
  }, [location.pathname, pingBackend]);

  const backendBaseUrl = `${backendOrigin.replace(/\/$/, '')}/api`;

  // -----------------------
  // Metrics data (ALL logic moved to hook)
  // -----------------------
  const {
    incomeMetrics,
    balanceMetrics,
    cashflowMetrics,
    metricsData,

    stockSymbol,
    priceDetails,
    sharePrice,
    sharePriceTimestamp,

    historicalSeries,
    visualQuarters,
    setVisualQuarters,

    availableQuarters,
    selectedMetricQuarter,
    setSelectedMetricQuarter,

    refreshMetrics,
    loading,
    errorMessage,
  } = useMetricsData({ userEmail });

  // -----------------------
  // Company / quarter selection
  // -----------------------
  const [selectedExchange, setSelectedExchange] = useState('NASDAQ');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedQuarter, setSelectedQuarter] = useState('');
  const [companies, setCompanies] = useState(() => getCompaniesForExchange('NASDAQ'));
  const [quarters, setQuarters] = useState([]);

  // -----------------------
  // Render
  // -----------------------
  return (
    <AppLayout
      isLoggedIn={isLoggedIn}
      setIsLoggedIn={setIsLoggedIn}
      handleLogout={handleLogout}
      onUpgradeClick={() => navigate('/subscribe')}
    >
      <AppRoutes
        isLoggedIn={isLoggedIn}
        setIsLoggedIn={setIsLoggedIn}

        PageLayout={PageLayout}

        // selection
        selectedExchange={selectedExchange}
        setSelectedExchange={setSelectedExchange}
        selectedCompany={selectedCompany}
        setSelectedCompany={setSelectedCompany}
        selectedQuarter={selectedQuarter}
        setSelectedQuarter={setSelectedQuarter}
        companies={companies}
        setCompanies={setCompanies}
        quarters={quarters}
        setQuarters={setQuarters}

        // metrics
        incomeMetrics={incomeMetrics}
        balanceMetrics={balanceMetrics}
        cashflowMetrics={cashflowMetrics}
        metricsData={metricsData}

        stockSymbol={stockSymbol}
        priceDetails={priceDetails}
        sharePrice={sharePrice}
        sharePriceTimestamp={sharePriceTimestamp}

        historicalSeries={historicalSeries}
        visualQuarters={visualQuarters}
        setVisualQuarters={setVisualQuarters}

        availableQuarters={availableQuarters}
        selectedMetricQuarter={selectedMetricQuarter}
        setSelectedMetricQuarter={setSelectedMetricQuarter}

        refreshMetrics={refreshMetrics}
        loading={loading}
        errorMessage={errorMessage}

        // config / misc
        companyOptions={companyOptions}
        userEmail={userEmail}
        backendBaseUrl={backendBaseUrl}
        pingBackend={pingBackend}
      />
    </AppLayout>
  );
}

export default App;

